import React from "react";
import { motion } from "framer-motion";
import { Calculator, LayoutDashboard, TrendingUp } from "lucide-react";

const BLUE = "#3B82F6";
const GOLD = "#C9A84C";
const GREY_WHITE = "#F8F9FA";
const GREY_WHITE_DARK = "#F1F3F4";
const TEXT_DARK = "#1F2937";
const TEXT_MEDIUM = "#4B5563";
const TEXT_LIGHT = "#6B7280";
const BORDER_LIGHT = "#E5E7EB";
const BG_DARK = "#07050E";

const STEPS = [
  {
    Icon: Calculator,
    headline: "Start with your business details",
    description: "See how much revenue you could be losing to failed payments. Instantly calculate your risk, then schedule a free setup call with our specialists to get started."
  },
  {
    Icon: LayoutDashboard,
    headline: "Your Crown Ledger account is created",
    description: "No complex setup, no new platform to learn. You'll get a custom Revenue Dashboard showing retried payments, successful recoveries, and branding options — all automated."
  },
  {
    Icon: TrendingUp,
    headline: "Your bank balance increases",
    description: "Sit back as profitability rises month after month. We'll send you a simple invoice — just 20% of the revenue we recover for you."
  }
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.10,
      duration: 0.60,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};

export default function ModernHowItWorks() {
  return (
    <>
      <style>{`
        .hiw-section {
          background: ${BG_DARK};
          padding: clamp(80px, 8vw, 120px) 24px clamp(80px, 8vw, 120px);
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .hiw-content {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 1200px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hiw-section .section-label {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 20px;
        }

        .hiw-section .section-rule {
          width: 60px;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.4) 50%,
            transparent 100%
          );
        }

        .hiw-section .section-label-text {
          font-family: 'Syne', system-ui, -apple-system, sans-serif;
          font-weight: 600;
          font-size: 11px;
          color: ${GREY_WHITE};
          text-transform: uppercase;
          letter-spacing: 0.14em;
        }

        .hiw-headline {
          text-align: center;
          max-width: 680px;
          margin: 0 auto 80px;
        }

        .hiw-headline h2 {
          font-family: 'Cabinet Grotesk', 'Outfit', sans-serif;
          font-weight: 700;
          font-size: clamp(32px, 3.6vw, 48px);
          color: ${GREY_WHITE};
          letter-spacing: -0.03em;
          line-height: 1.1;
          margin: 0 0 20px;
        }

        .hiw-headline p {
          font-family: 'Outfit', sans-serif;
          font-weight: 400;
          font-size: 18px;
          color: rgba(248, 249, 250, 0.7);
          line-height: 1.6;
          max-width: 54ch;
          margin: 0 auto;
        }

        .hiw-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          width: 100%;
          max-width: 1000px;
        }

        .hiw-card {
          background: #FFFFFF;
          border: 1px solid ${BORDER_LIGHT};
          border-radius: 20px;
          padding: 40px 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .hiw-card:hover {
          transform: translateY(-4px);
          border-color: ${BLUE};
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(59, 130, 246, 0.15);
        }

        .hiw-icon {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.1), rgba(201, 168, 76, 0.05));
          border: 1px solid rgba(201, 168, 76, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          transition: all 0.3s ease;
        }

        .hiw-card:hover .hiw-icon {
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.15), rgba(201, 168, 76, 0.08));
          border-color: rgba(201, 168, 76, 0.4);
          transform: scale(1.05);
        }

        .hiw-headline-text {
          font-family: 'Cabinet Grotesk', 'Outfit', sans-serif;
          font-size: 22px;
          font-weight: 700;
          color: ${BLUE};
          letter-spacing: -0.02em;
          line-height: 1.2;
          margin-bottom: 16px;
          transition: all 0.3s ease;
        }

        .hiw-card:hover .hiw-headline-text {
          color: #60A5FA;
        }

        .hiw-description {
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 400;
          line-height: 1.6;
          color: ${TEXT_MEDIUM};
        }

        /* Mobile swipe functionality */
        @media (max-width: 1024px) {
          .hiw-grid {
            display: flex;
            flex-wrap: nowrap;
            gap: 16px;
            max-width: 100%;
            overflow-x: auto;
            overflow-y: hidden;
            padding: 0 16px 8px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .hiw-grid::-webkit-scrollbar {
            display: none;
          }

          .hiw-card {
            flex: 0 0 calc(100% - 32px);
            scroll-snap-align: center;
            scroll-snap-stop: always;
          }
        }

        @media (max-width: 720px) {
          .hiw-grid {
            padding: 0 14px 8px;
          }

          .hiw-card {
            flex-basis: calc(100% - 28px);
            padding: 32px 24px;
          }
        }

        /* Mobile responsive */
        @media (max-width: 639px) {
          .hiw-section {
            padding: 60px 16px 80px;
          }

          .hiw-headline h2 {
            font-size: 28px;
          }

          .hiw-card {
            padding: 28px 20px;
          }

          .hiw-icon {
            width: 50px;
            height: 50px;
            margin-bottom: 20px;
          }

          .hiw-headline-text {
            font-size: 18px;
          }

          .hiw-description {
            font-size: 14px;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .hiw-card:hover {
            transform: none;
          }

          .hiw-card:hover .hiw-icon {
            transform: none;
          }
        }
      `}</style>

      <section id="how-it-works" className="hiw-section" data-testid="how-it-works-section">
        <div className="hiw-content">
          {/* Section label */}
          <div className="section-label">
            <div className="section-rule"></div>
            <span className="section-label-text">HOW IT WORKS</span>
            <div className="section-rule"></div>
          </div>

          {/* Section headline */}
          <motion.div 
            className="hiw-headline"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={0}
          >
            <h2>Start Recovering Revenue in Three Simple Steps</h2>
            <p>No complex setup. No upfront costs. Just results that impact your bottom line.</p>
          </motion.div>

          {/* Three-card grid */}
          <motion.div 
            className="hiw-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {STEPS.map((step, index) => (
              <motion.div
                key={index}
                className="hiw-card"
                variants={fadeUp}
                custom={index}
              >
                {/* Icon */}
                <div className="hiw-icon">
                  <step.Icon size={28} style={{ color: GOLD }} />
                </div>

                {/* Blue headline */}
                <h3 className="hiw-headline-text">{step.headline}</h3>

                {/* Description */}
                <p className="hiw-description">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
}
